import { makeAutoObservable, runInAction } from 'mobx'
import Geolocation, { GeoPosition } from 'react-native-geolocation-service'
import { GpsLocation } from 'infra/type'
import { getDistance } from 'geolib'

class LocationStore {
  _location: GpsLocation | null = null
  _watchId: number | null = null

  constructor() {
    makeAutoObservable(this)
  }

  _onLocationChange(position: GeoPosition) {
    const { latitude, longitude } = position.coords
    runInAction(
      () =>
        (this._location = { lat: String(latitude), lng: String(longitude) }),
    )
  }

  async getLocation(
    refresh: boolean,
    enableHighAccuracy: boolean = false,
  ): Promise<GpsLocation> {
    if (!refresh && this._location) return this._location
    const wasWatching = this._watchId !== null
    return new Promise((resolve, reject) => {
      // `getCurrentPosition` cannot be called while watching
      if (wasWatching) this.unwatchLocation()
      Geolocation.getCurrentPosition(
        (position) => {
          this._onLocationChange(position)
          const location = this._location
          if (!location) {
            reject(new Error('Location was unavailable after GPS callback'))
            if (wasWatching) this.watchLocation()
            return
          }
          resolve(location)
          if (wasWatching) this.watchLocation()
        },
        (e) => {
          reject(e)
          if (wasWatching) this.watchLocation()
        },
        {
          enableHighAccuracy,
          timeout: 20000,
          maximumAge: 0,
        },
      )
    })
  }

  watchLocation() {
    if (this._watchId !== null) {
      console.warn('[Location] Already watching!')
      return
    }
    // return immediately only at first call
    this._watchId = Geolocation.watchPosition(
      this._onLocationChange.bind(this),
      (e) => {
        console.error(`[Location] ${e}`)
      },
      { enableHighAccuracy: true },
    )
  }

  unwatchLocation() {
    if (this._watchId === null) return
    Geolocation.clearWatch(this._watchId)
    this._watchId = null
  }

  getDistance(loc: GpsLocation) {
    if (!this._location) return null
    return getDistance(this._location, loc)
  }
}

export const locationStore = new LocationStore()
